import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  homeWidgetRegistry,
  isHomeWidgetAvailable,
} from "@/pages/home/widgets/home-widget.registry.tsx";
import { ApiEmployeeHomeLayoutWidget } from "@/types/employee-settings.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Box, Checkbox, FontAwesomeIcon } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface HomeLayoutEditorProps {
  widgets: ApiEmployeeHomeLayoutWidget[];
  onChange: (widgets: ApiEmployeeHomeLayoutWidget[]) => void;
}

export const HomeLayoutEditor = ({ widgets, onChange }: HomeLayoutEditorProps) => {
  const { t } = useTranslation();
  const { canIEdit, isFeatureEnabled } = usePermissions();

  const isAvailable = (widget: ApiEmployeeHomeLayoutWidget) =>
    isHomeWidgetAvailable(widget.id, { canIEdit, isFeatureEnabled });

  const availableWidgets = widgets.filter(isAvailable);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = widgets.filter(isAvailable);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    let index = 0;
    onChange(widgets.map((widget) => (isAvailable(widget) ? reordered[index++] : widget)));
  };

  const handleToggle = (id: ApiEmployeeHomeLayoutWidget["id"], hidden: boolean) => {
    onChange(widgets.map((widget) => (widget.id === id ? { ...widget, hidden } : widget)));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={"home-widgets"}>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {availableWidgets.map((widget, index) => {
              const isLocked = widget.locked || widget.is_fixed;

              return (
                <Draggable key={widget.id} draggableId={widget.id} index={index}>
                  {(provided) => (
                    <Box
                      className={
                        "flex items-center justify-between gap-md my-2 px-4 py-1 border border-dark/5 rounded-xs bg-dark/5"
                      }
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <Checkbox
                        checked={!widget.hidden}
                        disabled={isLocked}
                        onChange={(e) => handleToggle(widget.id, !e.target.checked)}
                        title={t(homeWidgetRegistry[widget.id].titleKey)}
                      />
                      <FontAwesomeIcon
                        icon={"fa-light fa-grip-dots-vertical"}
                        className={"text-secondary mr-2"}
                      />
                    </Box>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
